/** Personal Finances cashflow snapshot for the home dashboard. */

export type PersonalDashboardHousehold = 'seco' | 'omhe';

export const PERSONAL_MIX_PILLARS = ['needs', 'wants', 'savings'] as const;
export type PersonalMixPillar = (typeof PERSONAL_MIX_PILLARS)[number];

export const PERSONAL_SPEND_PARTS = [
  'housing',
  'car',
  'groceries',
  'health',
  'insurance',
  'school',
  'finance',
  'dining',
  'lifestyle',
  'children',
  'travel',
  'savings',
  'pension',
] as const;
export type PersonalSpendPart = (typeof PERSONAL_SPEND_PARTS)[number];

export const PERSONAL_PILLAR_PARTS: Record<PersonalMixPillar, readonly PersonalSpendPart[]> = {
  needs: ['housing', 'car', 'groceries', 'health', 'insurance', 'school', 'finance'],
  wants: ['dining', 'lifestyle', 'children', 'travel'],
  savings: ['savings', 'pension'],
};

export const PERSONAL_MIX_LABELS: Record<PersonalMixPillar, string> = {
  needs: 'Needs',
  wants: 'Wants',
  savings: 'Savings',
};

export const PERSONAL_SPEND_PART_LABELS: Record<PersonalSpendPart, string> = {
  housing: 'Housing',
  car: 'Car',
  groceries: 'Groceries & household',
  health: 'Health',
  insurance: 'Insurance',
  school: 'School',
  finance: 'Bank fees & tax',
  dining: 'Dining & cafe',
  lifestyle: 'Lifestyle',
  children: 'Children',
  travel: 'Travel',
  savings: 'Savings',
  pension: 'Pension',
};

export const PERSONAL_MIX_COLORS: Record<PersonalMixPillar, string> = {
  needs: '#0F766E',
  wants: '#EA580C',
  savings: '#2563EB',
};

export interface PersonalSpendShare {
  amount: number;
  pct_of_income: number | null;
}

export interface PersonalMixPillarShare extends PersonalSpendShare {
  parts: Record<string, PersonalSpendShare>;
}

export interface PersonalDashboardMonth {
  month: string;
  label: string;
  income: number;
  expenses: number;
  net: number;
  spend?: Record<PersonalMixPillar, PersonalMixPillarShare>;
}

export interface PersonalDashboardHouseholdSummary {
  household: PersonalDashboardHousehold;
  label: string;
  months?: PersonalDashboardMonth[];
  spend?: Record<PersonalMixPillar, PersonalMixPillarShare>;
}

export function formatPersonalSpendPct(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return `${Math.round(Number(value))}%`;
}

export interface PersonalMixPartRow {
  part: PersonalSpendPart;
  label: string;
  amount: number;
  pct: number | null;
}

export interface PersonalMixRow {
  pillar: PersonalMixPillar;
  label: string;
  amount: number;
  pct: number | null;
  parts: PersonalMixPartRow[];
}

function mixPartShare(
  spend: Record<string, PersonalMixPillarShare> | null | undefined,
  pillar: PersonalMixPillar,
  part: PersonalSpendPart
): PersonalSpendShare {
  const cell = spend?.[pillar]?.parts?.[part];
  return {
    amount: Number(cell?.amount || 0),
    pct_of_income: cell?.pct_of_income ?? null,
  };
}

export function personalMixRows(
  spend: Record<string, PersonalMixPillarShare> | null | undefined
): PersonalMixRow[] {
  return PERSONAL_MIX_PILLARS.map((pillar) => {
    const parts = PERSONAL_PILLAR_PARTS[pillar]
      .map((part) => {
        const share = mixPartShare(spend, pillar, part);
        return {
          part,
          label: PERSONAL_SPEND_PART_LABELS[part],
          amount: share.amount,
          pct: share.pct_of_income,
        };
      })
      .filter((row) => row.amount !== 0);
    return {
      pillar,
      label: PERSONAL_MIX_LABELS[pillar],
      amount: Number(spend?.[pillar]?.amount || 0),
      pct: spend?.[pillar]?.pct_of_income ?? null,
      parts,
    };
  });
}

/** Personal Finances chart URL for a dashboard spend part (group and/or category). */
const PERSONAL_SPEND_URL: Record<PersonalSpendPart, { cf: string; grp?: string; cat?: string }> = {
  savings: { cf: 'internal-transfers', cat: 'savings' },
  housing: { cf: 'expenses', grp: 'housing' },
  car: { cf: 'expenses', grp: 'transport' },
  groceries: { cf: 'expenses', grp: 'living', cat: 'groceries,groceries-emergency,household' },
  health: { cf: 'expenses', grp: 'living', cat: 'medical,medical-natural,pharmacy,therapy' },
  insurance: { cf: 'expenses', grp: 'insurance' },
  school: { cf: 'expenses', grp: 'living', cat: 'school' },
  finance: { cf: 'expenses', grp: 'finance', cat: 'bank-fees,cash-atm,tax,income-tax,fx' },
  dining: { cf: 'expenses', grp: 'living', cat: 'dining,cafe,alcohol' },
  lifestyle: {
    cf: 'expenses',
    grp: 'living',
    cat: 'clothing,digital,pets,health-fitness,sports-leisure,crafting,hobbies-interests,gifts,books,night-out,entertainment,charity,wellness,retreats',
  },
  children: { cf: 'expenses', grp: 'children' },
  travel: { cf: 'expenses', grp: 'travel' },
  pension: { cf: 'expenses', grp: 'finance', cat: 'pension' },
};

export function personalSpendPartPath(
  household: PersonalDashboardHousehold,
  part: PersonalSpendPart
): string {
  const spec = PERSONAL_SPEND_URL[part];
  const params = new URLSearchParams();
  params.set('tab', household);
  params.set('cf', spec.cf);
  if (spec.grp) params.set('grp', spec.grp);
  if (spec.cat) params.set('cat', spec.cat);
  return `/personal-finances?${params.toString()}`;
}

export function personalSpendMonthsNewestFirst(
  months: PersonalDashboardMonth[] | null | undefined
): PersonalDashboardMonth[] {
  return [...(months || [])].sort((left, right) => String(right.month || '').localeCompare(String(left.month || '')));
}

/** Tightest month slot that still leaves a readable bar when filling the page. */
export const PERSONAL_CASHFLOW_MONTH_WIDTH = 36;
export const PERSONAL_CASHFLOW_MIN_MONTH_WIDTH = 16;
export const PERSONAL_CASHFLOW_Y_AXIS_WIDTH = 96;

export function personalCashflowPlotWidth(viewportWidth: number): number {
  return Math.max(0, Number(viewportWidth) || 0) - PERSONAL_CASHFLOW_Y_AXIS_WIDTH;
}

export function personalCashflowChartWidth(monthCount: number, minWidth = 480): number {
  const count = Math.max(0, Number(monthCount) || 0);
  const floor = Math.max(0, Number(minWidth) || 0);
  return Math.max(count * PERSONAL_CASHFLOW_MONTH_WIDTH, floor);
}

/** How many months fill the page at the given slot width. */
export function personalCashflowMonthsThatFit(
  viewportWidth: number,
  monthWidth = PERSONAL_CASHFLOW_MIN_MONTH_WIDTH
): number {
  const inner = personalCashflowPlotWidth(viewportWidth);
  const slot = Math.max(1, Number(monthWidth) || PERSONAL_CASHFLOW_MIN_MONTH_WIDTH);
  if (inner <= 0) return 0;
  return Math.max(1, Math.floor(inner / slot));
}

/** Latest months that fill the page; the table still uses the full series. */
export function personalCashflowChartHistory(
  months: PersonalDashboardMonth[] | null | undefined,
  viewportWidth?: number
): PersonalDashboardMonth[] {
  const rows = months || [];
  const cap = personalCashflowMonthsThatFit(Number(viewportWidth) || 0);
  if (!cap || rows.length <= cap) return rows;
  return rows.slice(-cap);
}

export function personalCashflowBarSize(monthCount: number, viewportWidth: number): number {
  const count = Math.max(1, Number(monthCount) || 1);
  const slot = personalCashflowPlotWidth(viewportWidth) / count;
  return Math.max(2, Math.min(8, Math.floor((slot - 3) / 2)));
}

export function personalCashflowXAxisInterval(monthCount: number, viewportWidth: number): number {
  const count = Math.max(1, Number(monthCount) || 1);
  const slot = personalCashflowPlotWidth(viewportWidth) / count;
  if (slot >= 16) return 0;
  if (slot >= 10) return 1;
  if (slot >= 7) return 2;
  return 5;
}

export interface PersonalCashflowChartMonth extends PersonalDashboardMonth {
  expensesChart: number;
}

/** Chart-only series: expenses plot below zero. Table values stay positive. */
export function personalCashflowChartRows(
  months: PersonalDashboardMonth[] | null | undefined
): PersonalCashflowChartMonth[] {
  return (months || []).map((row) => ({
    ...row,
    expensesChart: -Math.abs(Number(row.expenses) || 0),
  }));
}

export type PersonalMonthFigure = 'income' | 'expenses' | 'net';

export function personalMonthFigurePath(
  household: PersonalDashboardHousehold,
  month: string,
  figure: PersonalMonthFigure
): string {
  const key = String(month || '').slice(0, 7);
  const year = key.slice(0, 4);
  const params = new URLSearchParams();
  params.set('tab', household);
  if (/^\d{4}$/.test(year)) params.set('year', year);
  if (/^\d{4}-\d{2}$/.test(key)) params.set('month', key);
  if (figure === 'income') params.set('cf', 'income');
  else if (figure === 'expenses') params.set('cf', 'expenses');
  else params.set('cf', 'income,expenses');
  return `/personal-finances?${params.toString()}`;
}

export function personalCashflowBarFigure(dataKey: string): PersonalMonthFigure | null {
  const key = String(dataKey || '');
  if (key === 'income') return 'income';
  if (key === 'expenses' || key === 'expensesChart') return 'expenses';
  if (key === 'net') return 'net';
  return null;
}

export function personalCashflowBarPath(
  household: PersonalDashboardHousehold,
  month: string,
  dataKey: string
): string | null {
  const figure = personalCashflowBarFigure(dataKey);
  const key = String(month || '').slice(0, 7);
  if (!figure || !/^\d{4}-\d{2}$/.test(key)) return null;
  return personalMonthFigurePath(household, key, figure);
}
